import './App.css';
import './styles/fluent-collabspace-overrides.css';
import { Suspense, lazy, useContext, useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@fluentui/react';

import { GlobalStateProvider, GlobalStateContext } from './contexts/GlobalState';
import { globalStateActionType } from './contexts/GlobalState/reducer';
import { CategoryProvider } from './contexts/CategoryContext';
import { lightTheme, darkTheme } from './styles/collabspaceTheme';

import { configService } from './services/configService';

import { Localized } from './utilities/localizedStrings';
import { getPrimaryNavGroups } from './utilities/systemAdminNavigation';
import { getTokensFromSSO } from './utilities/identity';

import { Spinner } from './components/common/Spinner/Spinner';
import { PageNotFound } from './components/common/PageNotFound/PageNotFound';
import { TopBar } from './components/common/TopBar/TopBar';
import { PrimaryNav } from './components/common/PrimaryNav/PrimaryNav';
import { SignInScreen } from './components/common/SignIn/SignInScreen';
import { ToastProvider } from './components/common/Toast/ToastProvider';

const Dashboard = lazy(() => import('./pages/dashboard'));
const Diagnostics = lazy(() => import('./pages/diagnostics'));
const Configuration = lazy(() => import('./pages/configuration'));
const Tenants = lazy(() => import('./pages/tenants'));
const Users = lazy(() => import('./pages/users'));
const Licensing = lazy(() => import('./pages/licensing'));
const Audits = lazy(() => import('./pages/audits'));
const Apps = lazy(() => import('./pages/apps'));
const Analytics = lazy(() => import('./pages/analytics'));
const System = lazy(() => import('./pages/system'));

function App() {
    return (
        <GlobalStateProvider>
            <ToastProvider>
                <AppThemeManager />
            </ToastProvider>
        </GlobalStateProvider>
    );
}

const NAV_RAIL_STORAGE_KEY = 'cw.navCollapsed';

function readInitialNavRail() {
    try {
        return window.localStorage.getItem(NAV_RAIL_STORAGE_KEY) === '1';
    } catch {
        return false;
    }
}

function AppThemeManager() {
    const [state, dispatch] = useContext(GlobalStateContext);
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [signInError, setSignInError] = useState(null);
    const [isNavRail, setIsNavRail] = useState(readInitialNavRail);

    const onToggleNavRail = useCallback(() => {
        setIsNavRail((prev) => {
            const next = !prev;
            try {
                window.localStorage.setItem(NAV_RAIL_STORAGE_KEY, next ? '1' : '0');
            } catch {
                /* no-op */
            }
            return next;
        });
    }, []);

    useEffect(() => {
        if (state.isDarkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [state.isDarkMode]);

    const setSignInState = useCallback(() => {
        async function signIn() {
            try {
                setIsSigningIn(true);
                const cwTokens = await getTokensFromSSO(configService.appId);
                if (cwTokens) {
                    dispatch({
                        type: globalStateActionType.SIGNIN,
                        value: {
                            ...cwTokens
                        }
                    });
                }
            } catch (err) {
                setSignInError(err);
            } finally {
                setIsSigningIn(false);
            }
        }
        signIn();
    }, [dispatch, setIsSigningIn, setSignInError]);

    useEffect(
        setSignInState,
        [setSignInState]
    );

    const theme = state.isDarkMode ? darkTheme : lightTheme;

    if (state.identityToken && state.accessToken) {
        return (
            <ThemeProvider applyTo="body" theme={theme} style={{ height: '100%' }}>
                <div className="cw-App" data-nav-rail={isNavRail ? '1' : '0'}>
                    <TopBar />
                    <AppAuthenticated
                        isNavRail={isNavRail}
                        onToggleNavRail={onToggleNavRail}
                    />
                </div>
            </ThemeProvider>
        );
    }

    if (state.isSigningOut) {
        return (
            <ThemeProvider applyTo="body" theme={theme} style={{ height: '100%' }}>
                <div className="cw-SignOutScreen">
                    <Spinner
                        label={Localized.get('authentication-signingOut')}
                        appendEllipsis
                    />
                </div>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider applyTo="body" theme={theme}>
            <SignInScreen
                isSigningIn={isSigningIn}
                signInError={signInError}
                setIsSigningIn={setIsSigningIn}
                setSignInError={setSignInError}
            />
        </ThemeProvider>
    );
}

function AppAuthenticated({ isNavRail, onToggleNavRail }) {
    const [state] = useContext(GlobalStateContext);
    const primaryNavGroups = getPrimaryNavGroups(state.accessToken?.memberRoles);

    return (
        <Router>
            <CategoryProvider>
                <div className="cw-App-Body">
                    <PrimaryNav
                        navGroups={primaryNavGroups}
                        isRail={isNavRail}
                        onToggleRail={onToggleNavRail}
                    />
                    <section className="cw-App-Section-Main">
                        <Suspense fallback={<Spinner />}>
                            <Routes>
                                <Route exact path="/" element={<Dashboard />} />
                                <Route path="/diagnostics" element={<Diagnostics />} />
                                <Route path="/configuration" element={<Configuration />} />
                                <Route path="/tenants" element={<Tenants />} />
                                <Route path="/users" element={<Users />} />
                                <Route path="/licensing/*" element={<Licensing />} />
                                <Route path="/audits" element={<Audits />} />
                                <Route path="/system/*" element={<System />} />
                                <Route path="/analytics" element={<Analytics />} />
                                <Route path="/apps/*" element={<Apps />} />
                                <Route path="*" element={<PageNotFound />} />
                            </Routes>
                        </Suspense>
                    </section>
                </div>
            </CategoryProvider>
        </Router>
    );
}

export default App;
